import {
  useCallback, useState,
} from 'react';
import { useDispatch } from 'react-redux';
import type { BoundaryGroup } from '~/boundary/boundary.types';
import type { BoundaryId } from '~/store/boundaries/boundaryIdentifier.type';
import {
  boundaryItemsUpdateItem, boundaryItemsUpdateItems,
} from '~/store/boundaryItems/boundaryItems.actionCreators';
import { useMapBoundariesSelector } from '~/store/boundaryItems/boundaryItems.selectors';
import { useGetBoundaryTerritoryGroup } from '~/store/boundaryTerritoryGroups/boundaryTerritoryGroups.selectors';
import { useMapIdSelector } from '~/store/selectors/useMapIdSelector';
import { type Geometry } from '../../../_shared/types/polygon/polygon.types';
import {
  type BoundaryCombineGeometryRequest,
  type BoundaryCombineGeometryRequestItem,
  getBoundaryCombineGeometry,
  updateBoundaryGeometry,
} from '../../../store/boundaries/boundaries.repository';
import { createBoundaryStateItemFromBoundaryResponse } from '../../../store/boundaryItems/boundaryItems.factory';
import { type BoundaryStateItem } from '../../../store/boundaryItems/boundaryItems.state';
import { useClientIdSelector } from '../../../store/selectors/useClientIdSelector';
import { type BoundaryEditSubmitOption } from './boundaryEditSubmitModal.component';
import { BoundaryEditSubmitModalAction } from './boundaryEditSubmitModal.helpers';

type UseBoundaryEditActionsProps = {
  editedBoundaryTerritoryGroupId: number;
};

export const useBoundaryEditActions = ({ editedBoundaryTerritoryGroupId }: UseBoundaryEditActionsProps) => {
  const [isLoading, setIsLoading] = useState(false);

  const clientId = useClientIdSelector();
  const mapId = useMapIdSelector() ?? undefined;
  const dispatch = useDispatch();

  const boundaryItems = useMapBoundariesSelector();
  const { getBoundaryTerritoryGroupById } = useGetBoundaryTerritoryGroup();

  const editedBoundaryTerritoryGroup = getBoundaryTerritoryGroupById(editedBoundaryTerritoryGroupId);
  const editedBoundaryGroupId = editedBoundaryTerritoryGroup?.boundaryGroupId;

  const boundaryCombineGeometry = useCallback(async (request: BoundaryCombineGeometryRequest) => {
    if (!clientId) {
      throw Error('Missing clientId');
    }

    return await getBoundaryCombineGeometry(clientId, request);
  }, [clientId]);

  const bulkSubtractGeometry = useCallback(async (targetBoundaryId: BoundaryId, geometry: BoundaryCombineGeometryRequestItem[]) => {
    if (!clientId) {
      throw Error('Missing clientId');
    }

    if (!editedBoundaryGroupId) {
      return [];
    }

    const allBoundaries = editedBoundaryGroupId ? boundaryItems.get(editedBoundaryGroupId)?.values() : null;
    const otherBoundaries = allBoundaries?.filter(boundary => boundary.id !== targetBoundaryId) ?? [];

    const newBoundaryStateItems: BoundaryStateItem[] = [];
    try {
      for (const boundary of otherBoundaries) {
        const newSourceGeometry = await boundaryCombineGeometry({
          include: [{
            type: 'boundary',
            boundary_id: boundary.id,
            boundary_group_id: editedBoundaryGroupId,
          }],
          subtract: geometry,
        });

        const response = await updateBoundaryGeometry(clientId, boundary.id, newSourceGeometry?.geometry, mapId);
        newBoundaryStateItems.push(createBoundaryStateItemFromBoundaryResponse(response).boundaryStateItem);
      }
    }
    catch (e) {
      // skip updates with error
      console.error('Error while updating boundary geometry', e);
    }
    return newBoundaryStateItems;
  }, [boundaryCombineGeometry, boundaryItems, clientId, editedBoundaryGroupId, mapId]);

  const updateBoundary = useCallback(async (boundaryId: number, newGeometry: Geometry) => {
    if (!clientId) {
      throw Error('Missing clientId');
    }

    const response = await updateBoundaryGeometry(clientId, boundaryId, newGeometry, mapId);
    return createBoundaryStateItemFromBoundaryResponse(response, { enforceLabelBoundsRecalc: true });
  }, [clientId, mapId]);

  const editBoundary = useCallback(async (
    submitData: BoundaryEditSubmitOption,
    baseGeometry: BoundaryCombineGeometryRequestItem[],
    geometry: BoundaryCombineGeometryRequestItem[],
    _onSuccess: () => void, onError: () => void
  ) => {
    if (!editedBoundaryGroupId) {
      return;
    }

    setIsLoading(true);

    const onSuccess = (boundaryStateItem: BoundaryStateItem, etag: BoundaryGroup['etag']) => {
      dispatch(boundaryItemsUpdateItem(
        editedBoundaryGroupId,
        boundaryStateItem,
        etag
      ));
      setIsLoading(false);
      _onSuccess();
    };

    const onBulkSuccess = (boundaryStateItems: BoundaryStateItem[], etag: BoundaryGroup['etag']) => {
      dispatch(boundaryItemsUpdateItems(
        editedBoundaryGroupId,
        boundaryStateItems,
        etag,
      ));
      setIsLoading(false);
      _onSuccess();
    };

    try {
      if (submitData.action === BoundaryEditSubmitModalAction.AddToExistingTerritory) {
        const newGeometry = await boundaryCombineGeometry({
          include: [...baseGeometry, ...geometry],
          subtract: [],
        });

        updateBoundary(submitData.selectedBoundaryId, newGeometry?.geometry)
          .then((boundaryItemAndEtag) =>
            onSuccess(boundaryItemAndEtag.boundaryStateItem, boundaryItemAndEtag.etag)
          );
      }

      if (submitData.action === BoundaryEditSubmitModalAction.RemoveBoundariesFromTerritory) {
        const newGeometry = await boundaryCombineGeometry({
          include: baseGeometry,
          subtract: geometry,
        });

        updateBoundary(submitData.selectedBoundaryId, newGeometry?.geometry)
          .then((boundaryItemAndEtag) =>
            onSuccess(boundaryItemAndEtag.boundaryStateItem, boundaryItemAndEtag.etag)
          );
      }

      if (submitData.action === BoundaryEditSubmitModalAction.ReassignTerritory) {

        const subtractedBoundaryStateItems = await bulkSubtractGeometry(submitData.targetBoundaryId, geometry);

        const newTargetGeometry = await boundaryCombineGeometry({
          include: [...baseGeometry, ...geometry],
          subtract: [],
        });

        updateBoundary(submitData.targetBoundaryId, newTargetGeometry?.geometry)
          .then((updated) => onBulkSuccess(
            [updated.boundaryStateItem, ...subtractedBoundaryStateItems],
            updated.etag
          ));
      }
    }
    catch (e) {
      console.error('Error while editing boundary', e);
      setIsLoading(false);
      onError();
    }
  }, [boundaryCombineGeometry, bulkSubtractGeometry, dispatch, editedBoundaryGroupId, updateBoundary]);

  return {
    isLoading,
    editBoundary,
  };
};
